import React from "react";
import VariantPanel, { type VariantFact, type VariantItem } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface CylindricalItemCopy {
  title: string;
  text: string;
  facts: VariantFact[];
  imageAlt: string;
}
interface CylindricalGroupCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
  itemsHeading: string;
  axial: CylindricalItemCopy;
  circumferential: CylindricalItemCopy;
}
const copyData: Record<Lang, CylindricalGroupCopy> = {
  en: {
    eyebrow: "Continuous Slot Wedge Wire · Cylindrical Screens",
    title: "Cylindrical Wedge Wire Screens",
    lead: "Reinforced stainless steel cylindrical screens engineered with continuous V-wire slots to resist collapse under demanding industrial filtration cycles. Available in axial and circumferential slot orientations for rotary separation, scraper systems, and high-flow radial straining.",
    points: ["Continuous non-clogging V-wire geometry maintains high open area with low operational differential pressure.", "Resistance-welded profile wire and support rods prevent collapse under high surge pressure and mechanical scraping.", "All-stainless construction handles ultrasonic cleaning, backflushing, and chemical washing for long service life."],
    facts: [{
      label: "Slot Width",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Diameter Range",
      value: "10 – 1200 mm"
    }, {
      label: "Construction",
      value: "Resistance Welded V-Wire"
    }, {
      label: "Standard Alloys",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Industrial cylindrical stainless steel wedge wire filter screens and support cores",
    itemsHeading: "Slot Orientations",
    axial: {
      title: "Axial Slot Cylinders",
      text: "Continuous slots run parallel to the cylinder axis, providing clean linear tracks for scraper systems, internal/external mechanical scraping, and radial filtration.",
      facts: [{
        label: "Slot Width",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Orientation",
        value: "Parallel to Axis"
      }, {
        label: "Flow Direction",
        value: "FOTI / FITO Options"
      }, {
        label: "Standard Alloys",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Axial slot wedge wire cylindrical screen with longitudinal profile wire arrangement"
    },
    circumferential: {
      title: "Circumferential Slot Cylinders",
      text: "Surface wires run continuously around the circumference of the cylinder with axial support rods, engineered for rotary drums, trommels, and internal-feed screening cylinders.",
      facts: [{
        label: "Slot Width",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Orientation",
        value: "Around Circumference"
      }, {
        label: "Diameter Range",
        value: "25.4 – 1200 mm"
      }, {
        label: "Standard Alloys",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Stainless steel circumferential slot cylinder screen with continuous radial wedge wire for rotary trommels"
    }
  },
  zh: {
    eyebrow: "连续缝隙楔形丝 · 圆柱筛筒与滤筒",
    title: "圆柱形楔形丝筛网与滤筒",
    lead: "采用全焊接连续 V 型丝结构制造的高强度不锈钢圆柱滤筒，专为苛刻工况下的抗压溃与高负荷过滤而设计。提供轴向与环向两种缝隙走向，广泛适配旋转筛分、刮刀自清洗系统及大流量径向分离。",
    points: ["连续自清洁 V 型截面几何结构，保持高开孔率并显著降低运行初始压差。", "全电阻熔焊支撑筋条与轮廓丝紧密结合，有效抵御瞬态高压冲击与机械刮渣磨损。", "全不锈钢结构支持超声波清洗、反冲洗及酸碱化学浸洗，具备长期循环使用寿命。"],
    facts: [{
      label: "缝隙宽度",
      value: "0.02 – 5.0 mm"
    }, {
      label: "直径范围",
      value: "10 – 1200 mm"
    }, {
      label: "结构形式",
      value: "全电阻熔焊 V 型丝"
    }, {
      label: "标准材质",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "工业级高精度不锈钢圆柱楔形丝滤筒与内部支撑骨架",
    itemsHeading: "缝隙排列方向",
    axial: {
      title: "轴向缝隙滤筒",
      text: "连续缝隙沿圆柱轴向平行延伸，为自清洗刮刀、内/外部机械刮渣与径向流体分离提供平滑的线性导向表面。",
      facts: [{
        label: "缝隙宽度",
        value: "0.02 – 5.0 mm"
      }, {
        label: "缝隙走向",
        value: "轴向平行"
      }, {
        label: "流向配置",
        value: "FOTI / FITO 可选"
      }, {
        label: "标准材质",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "轴向缝隙楔形丝圆柱筛筒与平行排列结构"
    },
    circumferential: {
      title: "环向缝隙圆筒筛",
      text: "楔形丝沿圆筒圆周方向连续缠绕焊接，配置轴向支撑筋条，专为旋转转鼓筛、滚筒筛及内进料物料轴向推进分选而设计。",
      facts: [{
        label: "缝隙宽度",
        value: "0.02 – 5.0 mm"
      }, {
        label: "缝隙走向",
        value: "圆周环绕"
      }, {
        label: "直径范围",
        value: "25.4 – 1200 mm"
      }, {
        label: "标准材质",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "全周向环绕缝隙不锈钢圆筒筛网与转鼓分选结构"
    }
  },
  es: {
    eyebrow: "Alambre en cuña de ranura continua · Cilindros filtrantes",
    title: "Cilindros filtrantes de alambre en cuña",
    lead: "Cilindros reforzados de acero inoxidable con soldadura continua en hilo en V, diseñados para resistir el colapso en ciclos exigentes de filtración industrial. Disponibles en orientación de ranura axial y circunferencial para separación rotativa, raspado mecánico y filtración radial.",
    points: ["Geometría continua anticolmatación de hilo en V que mantiene una elevada superficie abierta con baja pérdida de carga.", "Estructura soldada por resistencia de hilo perfilado y varillas de soporte que previene el colapso ante sobrepresiones.", "Construcción íntegra en acero inoxidable apta para lavado por ultrasonidos, retrolavado y limpieza química."],
    facts: [{
      label: "Ancho de ranura",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Rango de diámetro",
      value: "10 – 1200 mm"
    }, {
      label: "Construcción",
      value: "Soldadura por resistencia en hilo en V"
    }, {
      label: "Aleaciones estándar",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Telas filtrantes cilíndricas industriales de alambre en cuña de acero inoxidable",
    itemsHeading: "Orientaciones de ranura",
    axial: {
      title: "Cilindros de ranura axial",
      text: "Las ranuras continuas corren paralelas al eje del cilindro, ofreciendo pistas lineales limpias para raspadores mecánicos y filtración radial de alto caudal.",
      facts: [{
        label: "Ancho de ranura",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Orientación",
        value: "Paralela al eje"
      }, {
        label: "Flujo",
        value: "Opciones FOTI / FITO"
      }, {
        label: "Aleaciones estándar",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Cilindro de alambre en cuña con ranura axial y perfiles longitudinales"
    },
    circumferential: {
      title: "Cilindros de ranura circunferencial",
      text: "Los alambres se enrollan de forma continua alrededor de la circunferencia con varillas axiales de soporte, diseñados para tromeles rotativos y tambores de alimentación interna.",
      facts: [{
        label: "Ancho de ranura",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Orientación",
        value: "Circunferencial"
      }, {
        label: "Rango de diámetro",
        value: "25.4 – 1200 mm"
      }, {
        label: "Aleaciones estándar",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Cilindro de ranura circunferencial de alambre en cuña para tamizado rotativo"
    }
  },
  de: {
    eyebrow: "Kontinuierlicher Spaltdraht · Spaltsiebzylinder",
    title: "Zylindrische Spaltsiebe & Filterzylinder",
    lead: "Verstärkte Edelstahlzylinder mit kontinuierlichen V-Draht-Schlitzen, konstruiert für maximale Kollapsfestigkeit in anspruchsvollen industriellen Filterzyklen. Erhältlich mit axialer oder umlaufender Schlitzausrichtung für Rotationssiebung, Abstreifersysteme und Radialfiltration.",
    points: ["Kontinuierliche verstopfungsfreie V-Draht-Geometrie sichert hohe freie Siebfläche bei minimalem Betriebsdruckverlust.", "Widerstandsgeschweißte Profil- und Tragstäbe widerstehen hohen Druckstößen und mechanischen Abstreifkräften.", "Voll-Edelstahlbauweise ermöglicht Ultraschallreinigung, Rückspülung und chemische Regenerierung für lange Standzeiten."],
    facts: [{
      label: "Spaltweite",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Durchmesserbereich",
      value: "10 – 1200 mm"
    }, {
      label: "Konstruktion",
      value: "Widerstandsgeschweißter V-Draht"
    }, {
      label: "Standard-Legierungen",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Industrielle zylindrische Edelstahl-Spaltsiebe und Filterstützkörbe",
    itemsHeading: "Schlitzausrichtungen",
    axial: {
      title: "Axialschlitz-Zylinder",
      text: "Kontinuierliche Schlitze verlaufen achsenparallel und bilden saubere Führungsbahnen für mechanische Abstreifer, Rückspülfilter und Radialdurchströmung.",
      facts: [{
        label: "Spaltweite",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Ausrichtung",
        value: "Parallel zur Achse"
      }, {
        label: "Strömungsrichtung",
        value: "FOTI / FITO Optionen"
      }, {
        label: "Standard-Legierungen",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Axialschlitz-Spaltsiebzylinder mit achsenparallelem Profildraht"
    },
    circumferential: {
      title: "Umfangsschlitz-Zylinder",
      text: "Profildrähte verlaufen kontinuierlich um den Zylinderumfang auf axialen Tragstäben, optimal für Trommelsiebe, Rotationsfilter und axialen Materialtransport.",
      facts: [{
        label: "Spaltweite",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Ausrichtung",
        value: "Umlaufend"
      }, {
        label: "Durchmesserbereich",
        value: "25.4 – 1200 mm"
      }, {
        label: "Standard-Legierungen",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Spaltsiebzylinder mit Umfangsschlitz für Trommelfiltration und Drehrohre"
    }
  },
  fr: {
    eyebrow: "Fil profilé à fente continue · Cylindres filtrants",
    title: "Cylindres filtrants en fil profilé",
    lead: "Cylindres en acier inoxydable renforcés conçus avec des fentes continues en fil profilé pour résister à l'écrasement lors des cycles de filtration industrielle sévères. Disponibles en orientation axiale ou circonférentielle pour la séparation rotative, les systèmes à racleur et la filtration radiale.",
    points: ["Géométrie continue autonettoyante en fil profilé assurant une surface ouverte maximale et une faible perte de charge.", "Assemblage soudé par résistance entre fils profilés et supports prévenant l'affaissement sous fortes surpressions.", "Fabrication intégrale en acier inoxydable compatible avec nettoyage ultrason, rétro-lavage et bains chimiques répétés."],
    facts: [{
      label: "Largeur de fente",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Plage de diamètres",
      value: "10 – 1200 mm"
    }, {
      label: "Fabrication",
      value: "Soudure par résistance de fil profilé"
    }, {
      label: "Alliages standards",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Tamis cylindriques industriels en fil profilé inox et éléments de renfort",
    itemsHeading: "Orientations des fentes",
    axial: {
      title: "Cylindres à fentes axiales",
      text: "Les fentes continues sont parallèles à l'axe du cylindre, offrant une surface linéaire lisse pour les racleurs mécaniques et la séparation radiale à fort débit.",
      facts: [{
        label: "Largeur de fente",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Orientation",
        value: "Parallèle à l'axe"
      }, {
        label: "Sens d'écoulement",
        value: "Options FOTI / FITO"
      }, {
        label: "Alliages standards",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Cylindre en fil profilé à fentes axiales et renforts longitudinaux"
    },
    circumferential: {
      title: "Cylindres à fente circonférentielle",
      text: "Les fils profilés s'enroulent autour de la circonférence sur des tiges axiales, conçus pour les trommels rotatifs, tambours et filtres à progression axiale.",
      facts: [{
        label: "Largeur de fente",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Orientation",
        value: "Circonférentielle"
      }, {
        label: "Plage de diamètres",
        value: "25.4 – 1200 mm"
      }, {
        label: "Alliages standards",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Cylindre en fil profilé à fente circonférentielle pour trommel rotatif"
    }
  },
  pt: {
    eyebrow: "Arame em cunha de ranhura contínua · Cilindros filtrantes",
    title: "Cilindros filtrantes de arame em cunha",
    lead: "Cilindros reforçados de aço inoxidável com fendas contínuas de arame perfilado em V para resistir ao colapso em ciclos exigentes de filtração industrial. Disponíveis em orientações de fenda axial e circunferencial para separação rotativa, raspadores mecânicos e filtração radial.",
    points: ["Geometria contínua antiobstrução de arame em V mantém alta área aberta com baixa perda de carga operacional.", "Perfis e barras de suporte soldados por resistência evitam colapso sob picos de pressão e raspagem mecânica.", "Construção 100% em aço inoxidável suporta limpeza ultrassônica, retrolavagem e lavagens químicas repetidas."],
    facts: [{
      label: "Abertura da fenda",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Faixa de diâmetro",
      value: "10 – 1200 mm"
    }, {
      label: "Construção",
      value: "Solda por resistência em arame em V"
    }, {
      label: "Ligas padrão",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Telas filtrantes cilíndricas industriais de arame em cunha e tubos de suporte",
    itemsHeading: "Orientações da fenda",
    axial: {
      title: "Cilindros de fenda axial",
      text: "Fendas contínuas paralelas ao eixo do cilindro oferecem guias lineares limpas para sistemas de raspadores mecânicos e filtração radial.",
      facts: [{
        label: "Abertura da fenda",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Orientação",
        value: "Paralela ao eixo"
      }, {
        label: "Configuração de fluxo",
        value: "Opções FOTI / FITO"
      }, {
        label: "Ligas padrão",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Cilindro de arame em cunha com fenda axial e perfis longitudinais"
    },
    circumferential: {
      title: "Cilindros de fenda circunferencial",
      text: "Arames de superfície enrolados continuamente na circunferência sobre barras axiais de suporte, projetados para trommels rotativos e cilindros de alimentação interna.",
      facts: [{
        label: "Abertura da fenda",
        value: "0.02 – 5.0 mm"
      }, {
        label: "Orientação",
        value: "Circunferencial"
      }, {
        label: "Faixa de diâmetro",
        value: "25.4 – 1200 mm"
      }, {
        label: "Ligas padrão",
        value: "AISI 304 / 316L"
      }],
      imageAlt: "Cilindro de arame em cunha com ranhura circunferencial para trommel rotativo"
    }
  }
};
export default function CylindricalGroup() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  const items: VariantItem[] = [{
    id: "axial-slot",
    title: copy.axial.title,
    text: copy.axial.text,
    facts: copy.axial.facts,
    href: "/AxialSlotCylindersPage",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
      alt: copy.axial.imageAlt
    }
  }, {
    id: "circumferential-slot",
    title: copy.circumferential.title,
    text: copy.circumferential.text,
    facts: copy.circumferential.facts,
    href: "/CircumferentialSlotCylindersPage",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
      alt: copy.circumferential.imageAlt
    }
  }];
  return <VariantPanel id="cylindrical-screens" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} href="/CylindricalScreensPage" image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
    alt: copy.imageAlt
  }} tone="background" itemsHeading={copy.itemsHeading} items={items} />;
}